import { useState, useEffect, useCallback } from "react";
import { api } from "../lib/api";

// Assessment templates. Named to keep it clear of useTemplates, which is the REPORT
// template hook — those control report layout, these control what an assessment asks.
// The design kit calls out the same distinction on screen.
export function useAssessmentTemplates() {
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const data = await api.get("s4/api/review-templates");
      setTemplates(data?.templates || []);
      setError("");
    } catch (e) {
      setError(e.message || "Could not load the templates.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const create = useCallback(async (fields) => {
    const data = await api.post("s4/api/review-templates", fields);
    await load();
    return data;
  }, [load]);

  // Publishing can fail for a reason worth showing: a category that is not yet full.
  // The error carries which one, so the caller can name it rather than just refusing.
  const publish = useCallback(async (id) => {
    try {
      const data = await api.post(`s4/api/review-templates/${id}/publish`);
      await load();
      return { ok: true, version: data?.version };
    } catch (e) {
      return { ok: false, error: e.message, incomplete: e.data?.incomplete || [] };
    }
  }, [load]);

  // Name, description and state. PATCH rather than PUT: PUT carries the template body,
  // and sending it without one would empty the template.
  const update = useCallback(async (id, patch) => {
    try {
      await api.patch(`s4/api/review-templates/${id}`, patch);
      await load();
      return { ok: true };
    } catch (e) {
      return { ok: false, error: e.message };
    }
  }, [load]);

  const remove = useCallback(async (id) => {
    try {
      await api.del(`s4/api/review-templates/${id}`);
      await load();
      return { ok: true };
    } catch (e) {
      // 409 means assessments were created from it — that is history, not clutter.
      return { ok: false, error: e.message };
    }
  }, [load]);

  return { templates, loading, error, reload: load, create, publish, update, remove };
}

// The item library, the second tab of the templates screen.
export function useReviewLibrary() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const data = await api.get("s4/api/review-library");
      setItems(data?.items || []);
      setError("");
    } catch (e) {
      setError(e.message || "Could not load the item library.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const create = useCallback(async (fields) => {
    await api.post("s4/api/review-library", fields);
    await load();
  }, [load]);

  const remove = useCallback(async (id) => {
    try {
      await api.del(`s4/api/review-library/${id}`);
      await load();
      return { ok: true };
    } catch (e) {
      return { ok: false, error: e.message, templates: e.data?.templates || [] };
    }
  }, [load]);

  const setEnabled = useCallback(async (id, enabled) => {
    await api.patch(`s4/api/review-library/${id}/active`, { enabled });
    await load();
  }, [load]);

  return { items, loading, error, reload: load, create, setEnabled, remove };
}

// One template, for the builder. The draft is edited locally and saved explicitly:
// autosaving a half-built category would mean a category briefly holding four items,
// which is a state the publish rule exists to prevent from ever being reachable.
export function useAssessmentTemplate(id) {
  const [template, setTemplate] = useState(null);
  const [versions, setVersions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    if (!id) return;
    setLoading(true);
    try {
      const data = await api.get(`s4/api/review-templates/${id}`);
      setTemplate(data?.template || null);
      setVersions(data?.versions || []);
      setError("");
    } catch (e) {
      setError(e.message || "Could not load the template.");
    } finally { setLoading(false); }
  }, [id]);

  useEffect(() => { load(); }, [load]);

  const save = useCallback(async (content, meta = {}) => {
    setBusy(true); setError("");
    try {
      await api.put(`s4/api/review-templates/${id}`, { ...meta, content });
      await load();
      return true;
    } catch (e) {
      setError(e.message || "Could not save the template.");
      return false;
    } finally { setBusy(false); }
  }, [id, load]);

  const publish = useCallback(async () => {
    setBusy(true); setError("");
    try {
      const data = await api.post(`s4/api/review-templates/${id}/publish`);
      await load();
      return { ok: true, version: data?.version };
    } catch (e) {
      // The server names which categories are short, so the builder can point at them
      // rather than just refusing.
      return { ok: false, error: e.message, incomplete: e.data?.incomplete || [] };
    } finally { setBusy(false); }
  }, [id, load]);

  return { template, versions, loading, error, busy, reload: load, save, publish };
}
